import { Paper } from '@mui/material';
import * as React from 'react';
import { API_SERVER } from '../../common/api';
import LoadingIcon from '../../common/component/LoadingIcon';
import { some } from '../../common/constants';
import useGeneralHook from '../../common/hook/useGeneralHook';
import { setLoading } from '../../common/redux/commonReducer';
import { fetchThunk } from '../../common/redux/thunk';
import ImageIndicationBox from '../component/imageIndication/ImageIndicationBox';
import PriceListFormDialog from '../component/imageIndication/PriceListFormDialog';
import TabCategory from '../component/imageIndication/TabCategory';
import { SYSTEM_CATEGORY_SCOPE, SYSTEM_CATEGORY_TYPE } from 'modules/common/constants';
import { useFetch } from 'modules/common/hook';

interface Props {}

const ImagingIndicationAdminPage: React.FunctionComponent<Props> = (props) => {
  const { dispatch, openSnackbar, confirmDialog, intl } = useGeneralHook();
  const [categoryListFormData, setCategoryListFormData] = React.useState<some | undefined>();
  const [categoryId, setCategoryId] = React.useState<number | undefined>();
  const { promptConfirmation, close } = confirmDialog;

  const {
    data = [],
    revalidate,
    isValidating,
  } = useFetch(
    API_SERVER.systemCategory.getList({
      scope: SYSTEM_CATEGORY_SCOPE.radiology,
      type: SYSTEM_CATEGORY_TYPE.priceList,
    }),
  );

  const onSubmitCategoryPlanDefinitionForm = React.useCallback(
    async (value: some) => {
      try {
        dispatch(setLoading(true));
        if (value.id) {
          await dispatch(
            fetchThunk(API_SERVER.systemCategory.update(value.id), 'put', {
              name: value.title,
              type: SYSTEM_CATEGORY_TYPE.priceList,
              scope: SYSTEM_CATEGORY_SCOPE.radiology,
            }),
          );
        } else {
          await dispatch(
            fetchThunk(API_SERVER.systemCategory.create(), 'post', {
              name: value.title,
              type: SYSTEM_CATEGORY_TYPE.priceList,
              scope: SYSTEM_CATEGORY_SCOPE.radiology,
            }),
          );
        }
        revalidate();
        openSnackbar({ message: intl.formatMessage({ id: 'updateSuccess' }) });
      } catch (e) {
        if (e) {
          openSnackbar({ message: e, type: 'error' });
        } else {
          openSnackbar({ message: intl.formatMessage({ id: 'updateFail' }), type: 'error' });
        }
      } finally {
        dispatch(setLoading(false));
        setCategoryListFormData(undefined);
      }
    },
    [dispatch, intl, openSnackbar, revalidate],
  );

  const onDeleteCategory = React.useCallback(
    async (value: some) => {
      const confirm = await promptConfirmation({
        warning: true,
        title: intl.formatMessage({ id: 'confirmDeleteTitle' }),
        content: intl.formatMessage({ id: 'confirmDeleteServicePrice' }, { name: value.name }),
      });
      if (confirm) {
        try {
          dispatch(setLoading(true));

          await dispatch(fetchThunk(API_SERVER.systemCategory.delete(value.id), 'delete'));

          revalidate();
          openSnackbar({ message: intl.formatMessage({ id: 'deleteSuccess' }) });
        } catch (e) {
          if (e) {
            openSnackbar({ message: e, type: 'error' });
          } else {
            openSnackbar({ message: intl.formatMessage({ id: 'deleteFail' }), type: 'error' });
          }
        } finally {
          dispatch(setLoading(false));
          setCategoryListFormData(undefined);
        }
      }
      close();
    },
    [close, dispatch, intl, openSnackbar, promptConfirmation, revalidate],
  );

  React.useEffect(() => {
    if (!categoryId && data.length > 0) {
      setCategoryId(data?.[0]?.id);
    }
  }, [categoryId, data]);

  return (
    <Paper style={{ flex: 1, overflow: 'auto' }}>
      <TabCategory
        data={data}
        onCreateOrUpdateCategory={setCategoryListFormData}
        onDeleteCategory={onDeleteCategory}
        categoryId={categoryId}
        setCategoryId={setCategoryId}
      />
      {!data && isValidating && <LoadingIcon />}
      {categoryId && <ImageIndicationBox categoryId={categoryId} />}
      <PriceListFormDialog
        open={!!categoryListFormData}
        formData={categoryListFormData}
        onSubmit={onSubmitCategoryPlanDefinitionForm}
        onClose={() => {
          setCategoryListFormData(undefined);
        }}
        categoryList={data}
      />
    </Paper>
  );
};

export default ImagingIndicationAdminPage;
